import { motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Housing } from './Card'
import { StatBlock } from './readout'

/**
 * The page-composition primitives.
 *
 * These exist because the same three shapes were being rebuilt per page, and they had
 * already started to drift — one page's section heading was a `CardHeader`, another's
 * was a bare `<p>`, and the two looked related only by accident. Centralising them is
 * what makes separate pages read as one product rather than as one person's idea of
 * the same idea, twice.
 *
 * The vocabulary is deliberately small:
 *
 *   Section    a titled region of a page
 *   Panel      a surface that groups rows — the only place a border is decorative
 *   Reveal     arrival, once, as the reader reaches it
 *
 * Anything that needs more than these probably wants to be its own component, not a
 * new variant here.
 */

/**
 * Section heading.
 *
 * A title-case heading with an optional note and control pushed right.
 *
 * ── Why this is no longer a mono eyebrow ──
 * It was a 10px uppercase mono label with a hairline rule running out to the note —
 * annotation rather than a title bar, on the argument that it should name the region and then
 * get out of the way so the figures carried the weight.
 *
 * The reference (defi.pots.money) does the opposite, and it is the direction this app is being
 * taken in: each section is named by a 20/30 w700 title-case heading with its control at the
 * right of the same row, and that heading is the second-loudest thing on the page after the
 * page title. Against six of those, a 10px tracked eyebrow did not read as the same KIND of
 * thing — one section announced itself and the next whispered, on the same screen.
 *
 * The hairline rule goes with it. A rule draws the section boundary, which is work the card
 * beneath the heading is already doing; keeping both draws the boundary twice.
 *
 * This matches `MetricRow`'s and `ChartSection`'s own headings by construction — same size,
 * same weight, same note-right arrangement — so a titled `MetricRow` and a hand-composed
 * section read as the same kind of region rather than as two different ideas.
 */
export function SectionHead({
  children,
  note,
  action,
}: {
  children: ReactNode
  note?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
      {/* 20/30 w700 — the same heading `CardHeader` renders, so a section and a card titled
          the same way cannot drift apart. Title case comes from dropping `.label`, which
          uppercased via CSS; the strings at every call site were already title case. */}
      <h2 className="title-section min-w-0">{children}</h2>
      {(note || action) && (
        <div className="flex shrink-0 items-center gap-3">
          {note && <span className="text-sm text-muted">{note}</span>}
          {action}
        </div>
      )}
    </div>
  )
}

/**
 * A grouped surface.
 *
 * ── This is `Housing`, and it always was ──
 * `Panel` and `Housing` rendered the same classes — `surface-glass rounded-[20px] ring-1
 * ring-line` — under two names, and the app used both. Some pages reached for one,
 * some for the other, Dashboard used both on the same screen. Two names for one surface is
 * how a design system starts drifting: nobody is doing anything wrong, but nobody can tell
 * which is correct either, so the padding gets decided at the call site instead.
 *
 * And it was: `p-4`, `p-5`, `p-6`, `p-8` and `p-10` all appeared as freehand overrides, so
 * two adjacent panels could disagree about their own inset by 24px. `Panel` now delegates to
 * `Housing` and takes `pad` on a FIXED SCALE — there is no arbitrary value to pass.
 *
 *   none  the panel wraps rows or a table that owns its own padding
 *   sm    a dense strip: a notice, a footnote row
 *   md    the default for a panel of content
 *   lg    a panel that is the page's main object
 *
 * `animate={false}`, unlike Housing's default. A Panel is usually a sub-element of something
 * that has already arrived, and a second entrance inside a card that just animated reads as
 * a stutter rather than as arrival.
 */
const PANEL_PAD = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
} as const

export function Panel({
  children,
  className,
  pad = 'none',
  /** Kept so existing `padded` call sites keep their inset. Prefer `pad`. */
  padded = false,
  animate = false,
}: {
  children: ReactNode
  className?: string
  pad?: keyof typeof PANEL_PAD
  padded?: boolean
  animate?: boolean
}) {
  return (
    <Housing
      animate={animate}
      className={cn('overflow-hidden', PANEL_PAD[padded ? 'sm' : pad], className)}
    >
      {children}
    </Housing>
  )
}

/**
 * Arrival on scroll.
 *
 * `once: true` is the important part — a region that re-animates every time it scrolls
 * back into view turns reading into a slideshow, and the second viewing is exactly when
 * someone is trying to compare two figures. The margin fires it slightly early so the
 * content has settled by the time it is actually being looked at.
 */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode
  delay?: number
  className?: string
}) {
  const reduced = useReducedMotion()

  return (
    <motion.div
      className={className}
      // Transform only — see cardEnter in ui/motion.ts.
      initial={reduced ? false : { y: 12 }}
      whileInView={{ y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  )
}

/**
 * A label/value pair.
 *
 * Delegates to `StatBlock` so the app has ONE headline-figure component rather than two that
 * drift. The tone names differ from StatBlock's by history — `accent`, `positive`, `danger` —
 * so they are mapped onto the semantic set here rather than renamed at every call site.
 */
export function Stat({
  label,
  value,
  unit,
  note,
  tone,
}: {
  label: string
  value: ReactNode
  unit?: string
  note?: string
  tone?: 'accent' | 'positive' | 'danger'
}) {
  const mapped =
    tone === 'accent' ? 'enamel' : tone === 'positive' ? 'fluid' : tone === 'danger' ? 'fault' : 'ink'

  return <StatBlock label={label} value={value} unit={unit} note={note} tone={mapped} size="sm" />
}
